import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';

import ChatPanel from './components/ChatPanel';
import Sidebar from './components/Sidebar';
import Login from './components/Login';
import ProjectDashboard from './components/ProjectManagement/ProjectDashboard';
import AdminSettings from './components/ProjectManagement/AdminSettings';
import { UserSession } from './types';

const App: React.FC = () => {
  const AUTH_LOGOUT_URL = `${process.env.REACT_APP_URL}/auth/logout`;
  const TOOL_ID = process.env.REACT_APP_TOOL_ID;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userSession, setUserSession] = useState<UserSession | null>(null);
  const [userEmail, setUserEmail] = useState('');
  const [userDomains, setUserDomains] = useState<string[]>([]);

  // Check if user is already logged in on mount
  useEffect(() => {
    const sessionData = localStorage.getItem('userSession');
    const adminId = localStorage.getItem('adminId');

    if (sessionData && adminId) {
      // Load session from localStorage
      const session: UserSession = JSON.parse(sessionData);
      // Try to load domains from separate storage key, fallback to session.domains
      let domains: string[] = [];
      const storedDomains = localStorage.getItem('userDomains');
      if (storedDomains) {
        try {
          const parsed = JSON.parse(storedDomains);
          if (Array.isArray(parsed)) {
            domains = parsed;
          }
        } catch {
          // ignore parse errors and fall back to session.domains
        }
      }
      if (!domains.length && Array.isArray(session.domains)) {
        domains = session.domains;
      }

      setIsAuthenticated(true);
      setUserSession(session);
      setUserEmail(session.email);
      setUserDomains(domains || []);
    }
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };

  const handleLoginSuccess = (session: UserSession) => {
    setIsAuthenticated(true);
    setUserSession(session);
    setUserEmail(session.email);
    setUserDomains(session.domains || []);
  };

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        await fetch(AUTH_LOGOUT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            refresh_token: refreshToken,
            tool_id: TOOL_ID,
          }),
        });
      }
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      localStorage.clear();
      setIsAuthenticated(false);
      setUserSession(null);
      setUserEmail('');
      setUserDomains([]);
    }
  };

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <Router>
      <div className="app" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Container fluid className="app-content" style={{ flex: 1, display: 'flex', padding: 0 }}>
          <div className={`sidebar-col ${sidebarCollapsed ? 'collapsed' : ''}`}>
            <Sidebar collapsed={sidebarCollapsed} toggleCollapse={toggleSidebar} userSession={userSession || undefined} />
          </div>

          <div className="main-content">
            <Routes>
              <Route path="/" element={<ChatPanel userEmail={userEmail} userDomains={userDomains} onLogout={handleLogout} userSession={userSession || undefined} />} />
              <Route path="/session/:sessionId" element={<ChatPanel userEmail={userEmail} userDomains={userDomains} onLogout={handleLogout} userSession={userSession || undefined} />} />
              <Route path="/projects" element={<ProjectDashboard userSession={userSession!} onLogout={handleLogout} />} />
              <Route path="/admin" element={<AdminSettings userSession={userSession!} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </Container>
      </div>
    </Router>
  );
};


export default App;
